import React, { Dispatch, FC, MouseEvent, SetStateAction } from 'react';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import styled from 'styled-components';
import Badge from '@mui/material/Badge';
import ToggleButton from '@mui/material/ToggleButton';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';

interface Props {
  kind: string;
  setKind: Dispatch<SetStateAction<'total' | 'applicant'>>;
  employeeReviewCount: number;
  applicantReviewCount: number;
}
const KununuBadge = styled(Badge)`
  margin-left: ${(props) => props.theme.spacing(2)};
  margin-right: ${(props) => props.theme.spacing(1.5)};
`;

const KununuToggleButton = styled(ToggleButton)`
  padding: ${(props) => props.theme.spacing(1)} ${(props) => props.theme.spacing(2)}
    ${(props) => props.theme.spacing(1)} ${(props) => props.theme.spacing(2)};
`;

const KununuToggleButtonGroup: FC<Props> = ({
  employeeReviewCount,
  applicantReviewCount,
  kind,
  setKind,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const handleChange = (event: MouseEvent<HTMLElement>, newValue: 'total' | 'applicant' | null) => {
    if (newValue !== null) {
      setKind(newValue);
    }
  };

  return (
    <ToggleButtonGroup
      color="primary"
      value={kind}
      exclusive
      onChange={handleChange}
      sx={{
        '& .Mui-selected.MuiToggleButtonGroup-grouped': {
          border: `1px solid ${theme.palette.primary.main}`,
        },
      }}
    >
      {/* kununu's headline score is employee-only from 2026-09-01, so Mitarbeiter uses kind "total". */}
      {/* Deliberately never disabled: this is the default view, and its badge count comes from the
          total row's total_employee, which a row written before that key existed (or by backdating)
          does not carry. A count of 0 must not make the default view unreachable; it self-heals on
          the next successful crawl. */}
      <KununuToggleButton
        value="total"
        size="small"
      >
        <Typography variant="body2">{t('employees')}</Typography>
        <KununuBadge
          showZero
          badgeContent={employeeReviewCount}
          color={employeeReviewCount === 0 ? 'default' : 'primary'}
          sx={{ pl: theme.spacing(1) }}
        />
      </KununuToggleButton>
      <KununuToggleButton
        disabled={applicantReviewCount === 0}
        value="applicant"
        size="small"
      >
        <Typography variant="body2">{t('applicants')}</Typography>
        <KununuBadge
          showZero
          badgeContent={applicantReviewCount}
          color={applicantReviewCount === 0 ? 'default' : 'primary'}
          sx={{ pl: theme.spacing(1), backgroundColor: theme.palette.error.main }}
        />
      </KununuToggleButton>
    </ToggleButtonGroup>
  );
};

export default KununuToggleButtonGroup;
